import type {Nullable} from '#types';

/**
 * Takes a string, returns an unsigned 32-bit number
 */
export function hashString(str: string) {
  let h = 0;

  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }

  return Math.abs(h);
}

type TransformRule = [string | RegExp, string];

/**
 * Transform a string to a consistent format
 * @param str - The string to transform
 * @param rules - The rules to apply to the string
 * @returns The transformed string
 */
export const transformString = (
  str: string,
  rules: Array<TransformRule>
): string => {
  return rules.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    str
  );
};

/**
 * Fuzzy match two strings
 * @param a - The first string
 * @param b - The second string
 * @returns True if the strings are fuzzy matches
 */
export const fuzzyMatch = (
  a: string,
  b: string,
  customRules?: Array<TransformRule>
): boolean => {
  const rules: Array<TransformRule> = [[/[\s_-]/g, ''], ...(customRules ?? [])];

  const lowerA = a.toLowerCase();
  const lowerB = b.toLowerCase();

  return (
    transformString(lowerA, rules).includes(transformString(lowerB, rules)) ||
    transformString(lowerB, rules).includes(transformString(lowerA, rules))
  );
};

/** Determines if a value is a nonempty string or not */
export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Truncate a string with ellipses
 * @param text - The text to truncate
 * @param maxLength - The maximum length of the text
 * @returns The truncated text
 */
export const truncateWithEllipses = (
  text: string,
  maxLength: number
): string => {
  if (text.length <= maxLength) return text;

  return `${text.substring(0, maxLength - 1)}...`;
};

export function safeJsonParse<T>(jsonString: Nullable<string>, fallback: T): T {
  if (!jsonString) return fallback;

  try {
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.warn('Failed to parse JSON props:', error);
    return fallback;
  }
}
